import React, { useState } from 'react';
import { X, PlusCircle, ChevronDown, ChevronRight, Group, Link, Unlink } from 'lucide-react';

// Enhanced FilterBuilder Component with Nested Logic Support
export const FilterBuilder = ({ label, filterKey, filters, onUpdate, placeholder }) => {
  const currentFilter = filters[filterKey] || { 
    logic: 'AND', 
    conditions: [],
    groups: [],
    connectors: [] // Array of connectors between items
  };
  
  // Ensure groups array exists
  const safeGroups = currentFilter.groups || [];
  const safeConditions = currentFilter.conditions || [];
  
  const [expandedGroups, setExpandedGroups] = useState({});
  const safeConnectors = currentFilter.connectors || [];

  const addCondition = (groupId = null) => {
    const newCondition = { type: 'contains', value: '', id: Date.now() };
    
    if (groupId) {
      // Add to specific group
      const newGroups = safeGroups.map(group => 
        group.id === groupId 
          ? { ...group, conditions: [...(group.conditions || []), newCondition] }
          : group
      );
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, groups: newGroups }
      });
    } else {
      // Add to root level
      const newConditions = [...safeConditions, newCondition];
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, conditions: newConditions }
      });
    }
  };

  const addGroup = () => {
    const newGroup = {
      id: Date.now(),
      logic: 'AND',
      conditions: [{ type: 'contains', value: '', id: Date.now() }]
    };
    
    // Add connector if there are existing items
    const hasExistingItems = safeConditions.length > 0 || safeGroups.length > 0;
    const newConnectors = hasExistingItems ? 
      [...safeConnectors, { type: 'AND', id: Date.now() }] : safeConnectors;
    
    onUpdate({
      ...filters,
      [filterKey]: { 
        ...currentFilter, 
        groups: [...safeGroups, newGroup],
        connectors: newConnectors
      }
    });
  };

  const updateCondition = (index, field, value, groupId = null) => {
    if (groupId) {
      const newGroups = safeGroups.map(group => 
        group.id === groupId 
          ? {
              ...group,
              conditions: (group.conditions || []).map((condition, i) => 
                i === index ? { ...condition, [field]: value } : condition
              )
            }
          : group
      );
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, groups: newGroups }
      });
    } else {
      const newConditions = [...safeConditions];
      newConditions[index] = { ...newConditions[index], [field]: value };
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, conditions: newConditions }
      });
    }
  };

  const removeCondition = (index, groupId = null) => {
    if (groupId) {
      const newGroups = safeGroups.map(group => 
        group.id === groupId 
          ? {
              ...group,
              conditions: (group.conditions || []).filter((_, i) => i !== index)
            }
          : group
      ).filter(group => (group.conditions || []).length > 0);
      
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, groups: newGroups }
      });
    } else {
      const newConditions = safeConditions.filter((_, i) => i !== index);
      if (newConditions.length === 0 && safeGroups.length === 0) {
        // Remove the filter entirely if no conditions or groups
        const newFilters = { ...filters };
        delete newFilters[filterKey];
        onUpdate(newFilters);
      } else {
        onUpdate({
          ...filters,
          [filterKey]: { ...currentFilter, conditions: newConditions }
        });
      }
    }
  };

  const updateLogic = (newLogic, groupId = null) => {
    if (groupId) {
      const newGroups = safeGroups.map(group => 
        group.id === groupId ? { ...group, logic: newLogic } : group
      );
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, groups: newGroups }
      });
    } else {
      onUpdate({
        ...filters,
        [filterKey]: { ...currentFilter, logic: newLogic }
      });
    }
  };

  const removeGroup = (groupId) => {
    const newGroups = safeGroups.filter(group => group.id !== groupId);
    onUpdate({
      ...filters,
      [filterKey]: { ...currentFilter, groups: newGroups }
    });
  };

  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const updateConnector = (connectorIndex, newType) => {
    const newConnectors = [...safeConnectors];
    newConnectors[connectorIndex] = { ...newConnectors[connectorIndex], type: newType };
    onUpdate({
      ...filters,
      [filterKey]: { ...currentFilter, connectors: newConnectors }
    });
  };

  const getConnectorForIndex = (index) => {
    // Connectors are between items, so index 0 has no connector
    // Index 1 has connector at position 0, index 2 has connector at position 1, etc.
    return safeConnectors[index - 1] || { type: 'AND' };
  };

  const renderCondition = (condition, index, groupId = null) => (
    <div key={condition.id || index} className="flex items-center space-x-2">
      <select
        value={condition.type}
        onChange={(e) => updateCondition(index, 'type', e.target.value, groupId)}
        className="text-xs border border-gray-300 rounded px-2 py-1 w-24"
      >
        <option value="contains">contains</option>
        <option value="startsWith">starts with</option>
        <option value="endsWith">ends with</option>
        <option value="exact">exactly</option>
        <option value="regex">regex</option>
      </select>
      <input
        type="text"
        value={condition.value}
        onChange={(e) => updateCondition(index, 'value', e.target.value, groupId)}
        placeholder={placeholder}
        className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
      />
      <button
        type="button"
        onClick={() => removeCondition(index, groupId)}
        className="text-red-600 hover:text-red-800"
        title="Remove condition"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  const renderGroup = (group) => {
    const isExpanded = expandedGroups[group.id] !== false;
    
    return (
      <div key={group.id} className="border border-blue-200 rounded-lg p-3 bg-blue-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => toggleGroupExpansion(group.id)}
              className="text-blue-600 hover:text-blue-800"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <span className="text-xs font-medium text-blue-700">Group</span>
            <select
              value={group.logic}
              onChange={(e) => updateLogic(e.target.value, group.id)}
              className="text-xs border border-blue-300 rounded px-2 py-1"
              disabled={(group.conditions || []).length < 2}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
              <option value="NOT">NOT</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => addCondition(group.id)}
              className="text-blue-600 hover:text-blue-800"
              title="Add condition to group"
            >
              <PlusCircle className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => removeGroup(group.id)}
              className="text-red-600 hover:text-red-800"
              title="Remove group"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {isExpanded && (
          <div className="space-y-2">
            {(group.conditions || []).map((condition, index) => renderCondition(condition, index, group.id))}
            {(group.conditions || []).length === 0 && (
              <div className="text-xs text-gray-500 italic">No conditions in group</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const hasContent = safeConditions.length > 0 || safeGroups.length > 0;

  // Create a combined list of all items (conditions and groups) with their connectors
  const allItems = [
    ...safeConditions.map((condition, index) => ({ type: 'condition', data: condition, index })),
    ...safeGroups.map((group, index) => ({ type: 'group', data: group, index: safeConditions.length + index }))
  ];

  const renderConnector = (itemIndex) => {
    const connector = getConnectorForIndex(itemIndex);
    return (
      <div key={`connector-${itemIndex}`} className="flex items-center justify-center py-1">
        <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full">
          <span className="text-xs text-gray-500">Connect with:</span>
          <select
            value={connector.type}
            onChange={(e) => updateConnector(itemIndex - 1, e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
          >
            <option value="AND">AND</option>
            <option value="OR">OR</option>
            <option value="NOT">NOT</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-gray-700">{label}</label>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={addGroup}
            className="text-green-600 hover:text-green-800"
            title="Add group"
          >
            <Group className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => addCondition()}
            className="text-blue-600 hover:text-blue-800"
            title="Add condition"
          >
            <PlusCircle className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!hasContent ? (
        <div className="text-xs text-gray-500 italic">No conditions set</div>
      ) : (
        <div className="space-y-2">
          {allItems.map((item, index) => (
            <div key={`${item.type}-${item.data.id || item.index}`}>
              {/* Show connector for all items except the first */}
              {index > 0 && renderConnector(index)}
              
              {/* Render the item */}
              {item.type === 'condition' ? 
                renderCondition(item.data, item.index) : 
                renderGroup(item.data)
              }
            </div>
          ))}
        </div>
      )}
      
      {/* Logic explanation */}
      {hasContent && (
        <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-600">
          <strong>Note:</strong> Use the connectors between items to specify how they should be combined
        </div>
      )}
    </div>
  );
};
